// ============================================================
// SettlementPreview.tsx — the live deductions preview (Stage 4; layout =
// the chosen mockup, option A + option C's breakdown bar).
// POST /settlement/preview runs the tested engine (settlementMath) on the
// saved inputs + the section's unsaved edits; it writes nothing.
//
// Reads as a STORY:
//   1 · Damages            total, with its heads
//   2 · Less, paid out     CRU repaid, interim payments (+ retention)
//   3 · Deductions         each one with a short "why" (and what it was cut
//                          from, struck through, when a cap bit)
//   → Net to client
// plus "Where the £X goes" — a horizontal bar of the same split (breakdown.ts)
// with a legend giving every amount AS TEXT (not colour alone), and a "Full
// calculation" disclosure with the engine's own rows and rules.
//
// Kept: "DRAFT — not for client use" (header + net tile); the greyed-out
// "out of date" state when the latest run fails; blocking errors (red) and
// warnings (amber), the child / protected-party message said ONCE; the
// "preview only, not saved" settlement date; the unreviewed-CFA flag.
// When the section is narrow (CaseSettlementSection SETTLEMENT_TWO_COL_MIN) it shows <SettlementBottomBar> instead — Net to
// client + "Show breakdown", which opens this same preview in a modal.
//
// (Batch 2) Runs on the SAVED figures plus the one card being edited — the
// "Includes unsaved changes" badge says so while a card is open. The CFA
// check is asked for ONCE, on the CFA card: here only a short line pointing
// there (and the engine's CFA_NOT_REVIEWED warning is not repeated). "No CFA"
// carries its own "Create from firm defaults" button. PAYEE-AWARE: for a
// child / protected party the net figure is labelled by where it goes
// (netLabelOf — "To the Court Funds Office"), "(proposed)" until a court
// order is recorded on the statement.
// ============================================================
import React, { useId } from 'react'
import DateInput from '../shared/DateInput'
import ModalFrame from '../shared/ModalFrame'
import ActionBox from '../shared/ActionBox'
import { BIcon, CARD, LABEL, NOTE, INPUT, money, Skel, Spinner, B_PRIMARY } from '../billing/billingUi'
import { SIcon } from '../settings/settingsUi'
import { Banner, DraftBadge, PenceField } from './settlementUi'
import { breakdownSegments, segmentWidthPct, type SegmentKey } from './breakdown'
import { CFA_STAGE_LABEL, type NetDestination, type PreviewOptions, type PreviewResult, type WaterfallRow } from '../../types/settlement.types'
import { netSumLabel, type Capacity, type RepresentativeType } from '../../types/settlementStatement.types'

// Who the net sum goes to, for the labels (the statement's own rule —
// backend lib/settlementStatement defaultDestination — unless the preview's
// option or the draft statement says otherwise).
export interface Payee {
  statementDestination?: NetDestination | null
  representativeType?: RepresentativeType | null
  representativeNamed?: boolean
  courtOrderRecorded?: boolean
}
export const payeeDestination = (capacity: Capacity, chosen: NetDestination | undefined, p: Payee | undefined): NetDestination => {
  if (chosen) return chosen
  if (p?.statementDestination) return p.statementDestination
  if (capacity === 'CHILD') return 'COURT_FUNDS_OFFICE'
  if (capacity === 'PROTECTED_PARTY') {
    return p?.representativeNamed && (p.representativeType === 'DEPUTY' || p.representativeType === 'LPA_ATTORNEY') ? 'DEPUTY' : 'COURT_FUNDS_OFFICE'
  }
  return 'CLIENT'
}
// "Net to client" for an adult; "To the Court Funds Office (proposed)" for a
// child before the court order, without "(proposed)" once it is recorded.
export const netLabelOf = (capacity: Capacity | null | undefined, chosen: NetDestination | undefined, p: Payee | undefined): string => {
  if (!capacity || capacity === 'ADULT') return 'Net to client'
  const label = netSumLabel(payeeDestination(capacity, chosen, p), p?.representativeType)
  return p?.courtOrderRecorded ? label : `${label} (proposed)`
}

// The badge on the preview while a card's draft is included.
export const UnsavedBadge: React.FC = () => (
  <span role="status" className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-2 py-[2px] rounded-[5px] whitespace-nowrap border border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn">
    <i className="w-[6px] h-[6px] rounded-full bg-casevo-warn" aria-hidden="true" />Includes unsaved changes
  </span>
)

const SECTION_LABEL: Record<WaterfallRow['section'], string> = {
  COSTS: 'Base-costs position', CREDIT: 'Credited to the client',
  DAMAGES: 'Damages', LESS: 'Less', CAP: 'Caps and limits', DEDUCTION: 'Deductions', TOTAL: 'Totals',
}
const DEST_LABEL: Record<NetDestination, string> = { CLIENT: 'Paid to the client', COURT_FUNDS_OFFICE: 'Court Funds Office', DEPUTY: 'Court of Protection deputy', CHILD_ACCOUNT: 'The child’s own account' }

// Bar colours (casevo-* tokens). The legend carries every amount as text.
export const SEGMENT_CLASS: Record<SegmentKey, string> = {
  CRU: 'bg-casevo-text-soft/60',
  INTERIM: 'bg-casevo-light/70',
  RETENTION: 'bg-casevo-mid/40',
  SUCCESS_FEE: 'bg-casevo-gold',
  COUNSEL: 'bg-casevo-gold/45',
  SHORTFALL: 'bg-casevo-gold/70',
  ATE: 'bg-casevo-warn/80',
  DISBURSEMENTS: 'bg-casevo-warn/50',
  ADVERSE: 'bg-casevo-danger/70',
  NET: 'bg-casevo-primary',
}

const pounds0 = (pence: number): string => `£${Math.round(pence / 100).toLocaleString('en-GB')}`

interface Props {
  preview: PreviewResult | null
  loading: boolean
  error: string | null
  options: PreviewOptions
  setOptions: (o: PreviewOptions) => void
  today: string
  // A migrated CFA nobody has reviewed yet → amber flag in the net tile.
  cfaUnreviewed?: boolean
  onReviewCfa?: () => void
  // Bits of the CURRENT figures the story names (from the page's drafts).
  heads?: { pslaPence: number; pastLossPence: number; futureLossPence: number; interestPence: number }
  firmCapLabel?: string | null // e.g. "30%" when the CFA has a firm cap
  // (Batch 2) A card's unsaved draft is in this run.
  unsaved?: boolean
  // (Batch 2) "No CFA" → create one here.
  onCreateCfa?: () => void
  creatingCfa?: boolean
  createCfaError?: string | null
  // (Batch 2) Payee-aware labels for a child / protected party.
  payee?: Payee
}

const ProposedTag: React.FC = () => (
  <span className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.06em] px-2 py-[2px] rounded-[4px] bg-casevo-warn-bg text-casevo-warn whitespace-nowrap">
    Proposed — subject to court order
  </span>
)

// "Where the £X goes" — the bar + its legend.
export const BreakdownBar: React.FC<{ figures: Parameters<typeof breakdownSegments>[0] }> = ({ figures }) => {
  const { totalPence, barTotalPence, segments } = breakdownSegments(figures)
  if (totalPence <= 0) return null
  return (
    <figure className="m-0 mt-[14px]" aria-label={`Where the ${pounds0(totalPence)} goes`}>
      <figcaption className="text-[12.5px] font-semibold text-casevo-forest mb-[6px]">Where the {pounds0(totalPence)} goes</figcaption>
      <div className="flex h-[14px] rounded-[7px] overflow-hidden gap-[2px] bg-casevo-border-soft" aria-hidden="true">
        {segments.map((s) => <span key={s.key} className={`h-full ${SEGMENT_CLASS[s.key]}`} style={{ width: `${segmentWidthPct(s.pence, barTotalPence)}%` }} />)}
      </div>
      <dl className="m-0 mt-[8px] grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-x-[18px] gap-y-[4px] text-[12px]">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-[6px] min-w-0">
            <span aria-hidden="true" className={`w-[9px] h-[9px] rounded-[2px] shrink-0 ${SEGMENT_CLASS[s.key]}`} />
            <dt className="text-casevo-text-soft truncate">{s.label}</dt>
            <dd className={`m-0 ml-auto tabular-nums ${s.key === 'NET' ? 'font-semibold text-casevo-forest' : 'text-casevo-forest'}`}>{pounds0(s.pence)}</dd>
          </div>
        ))}
      </dl>
    </figure>
  )
}

// A plain-English "why" for a deduction row, plus what cut it (if a cap bit).
const cutReason = (rule: string): string =>
  /Statutory cap/i.test(rule) ? 'the statutory cap'
    : /Firm total cap/i.test(rule) ? 'the firm’s own cap'
      : /CPR 21/i.test(rule) || /Court-approved/i.test(rule) ? 'the court limit for children'
        : /damages available/i.test(rule) ? 'what’s left of the damages'
          : /counselInCap|firm bears/i.test(rule) ? 'the CFA (not passed on)'
            : rule
const whyOf = (row: WaterfallRow, ctx: { ratePct: number; capBase: number; capPct: string }): string => {
  const cuts = Array.from(new Set(row.reductions.map((r) => cutReason(r.rule))))
  const cut = cuts.length ? `, cut by ${cuts.join(' and ')}` : ''
  switch (row.key) {
    case 'SUCCESS_FEE': return `${ctx.ratePct}% uplift on ${/recovered base/.test(row.rule) ? 'the base costs recovered' : 'all the firm’s base costs'}${row.reductions.some((r) => /Statutory cap/i.test(r.rule)) ? `, capped at ${ctx.capPct} of ${money(ctx.capBase)}` : ''}${cuts.filter((c) => c !== 'the statutory cap').length ? `, cut by ${cuts.filter((c) => c !== 'the statutory cap').join(' and ')}` : ''}`
    case 'COUNSEL_SUCCESS_FEE': return `Counsel’s uplift, passed on under the CFA${cut}`
    case 'SHORTFALL': return `The firm’s costs not covered by the other side${/interim/.test(row.rule) ? ' or by interim bills' : ''}${cut}`
    case 'ATE_PREMIUM': return `Insurance premium the other side didn’t pay${cut}`
    case 'DISBURSEMENTS': return `Expenses the other side didn’t pay back${cut}`
    case 'ADVERSE_COSTS_OFFSET': return `Costs the court ordered the client to pay (QOCS)${cut}`
    default: return row.rule
  }
}
const CONFIRM_WITH_SOLICITOR = 'Confirm with solicitor.'
const STORY_LABEL: Record<string, string> = {
  SUCCESS_FEE: 'Success fee', COUNSEL_SUCCESS_FEE: 'Counsel’s success fee', SHORTFALL: 'Costs not recovered',
  ATE_PREMIUM: 'ATE premium', DISBURSEMENTS: 'Unrecovered disbursements', ADVERSE_COSTS_OFFSET: 'Adverse costs (QOCS)',
}

const StoryHead: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <div className="mt-[16px] mb-[2px] pb-[5px] border-b border-casevo-border-soft text-[10.5px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">{n} · {children}</div>
)
const StoryRow: React.FC<{ title: React.ReactNode; why?: React.ReactNode; amount: string; was?: string | null; minus?: boolean; mark?: React.ReactNode }> = ({ title, why, amount, was, minus = true, mark }) => (
  <div className="grid grid-cols-[22px_minmax(0,1fr)_auto] gap-x-[10px] items-start py-[8px] border-b border-casevo-border-soft last:border-b-0">
    <span aria-hidden="true" className="mt-[1px] w-[20px] h-[20px] rounded-full border border-casevo-border text-casevo-text-soft grid place-items-center text-[11px]">{mark ?? (minus ? '−' : '£')}</span>
    <div className="min-w-0">
      <div className="text-[13.5px] text-casevo-forest">{title}</div>
      {why && <div className="text-[12px] text-casevo-text-soft leading-snug mt-[1px]">{why}</div>}
    </div>
    <div className="text-right whitespace-nowrap">
      <div className="text-[13.5px] font-semibold tabular-nums text-casevo-forest">{amount}</div>
      {was && <div className="text-[11.5px] tabular-nums text-casevo-text-soft line-through">{was}</div>}
    </div>
  </div>
)

// The preview's body (no card chrome) — shared by the side column and the
// bottom bar's "Show breakdown" modal.
export const PreviewBody: React.FC<Props> = ({ preview, loading, error, options, setOptions, today, cfaUnreviewed, onReviewCfa, heads, firmCapLabel, onCreateCfa, creatingCfa, createCfaError, payee }) => {
  const r = preview?.result ?? null
  // The last run failed: what's on screen no longer matches the inputs.
  const stale = !!error && !!r
  const staleCls = stale ? 'opacity-40 grayscale' : ''
  // Deductions are "proposed" until the court order is recorded.
  const proposed = !!r && r.capacity !== 'ADULT' && !payee?.courtOrderRecorded
  const netLabel = netLabelOf(r?.capacity, options.kind === 'INTERIM' ? undefined : options.netDestination, payee)
  const net = r?.figures.netToClientPence ?? null
  const f = r?.figures
  const ratePct = preview ? preview.used.rate.rateBasisPoints / 100 : 0
  const capRow = r?.rows.find((x) => x.key === 'STATUTORY_CAP')
  const capPct = capRow?.label.match(/\((\d+%)\)/)?.[1] ?? '25%'
  const deductionRows = r ? r.rows.filter((x) => x.section === 'DEDUCTION' && x.finalPence > 0) : []
  const creditRow = r?.rows.find((x) => x.key === 'COSTS_CREDIT')
  const disbursementCreditRow = r?.rows.find((x) => x.key === 'DISBURSEMENT_CREDIT')
  const cruRow = r?.rows.find((x) => x.key === 'CRU')
  const interimRow = r?.rows.find((x) => x.key === 'INTERIM_PAYMENTS')
  // The body renders twice (side column + the bottom bar's modal): ids are per copy.
  const uid = useId()
  return (
    <>
      {/* Options the preview is run with (not saved). */}
      <div className="flex flex-col gap-[12px] pb-[14px] border-b border-casevo-border-soft">
        {/* Date + kind share a line when there's room; the kind wraps under the date in a narrow column. */}
        <div className="flex flex-wrap gap-[12px]">
        <div className="w-[236px] shrink-0">
          <span className={LABEL}>Settlement date</span>
          <DateInput value={options.settlementDate ?? today} allowFuture ariaLabel="Settlement date" onChange={(v) => setOptions({ ...options, settlementDate: v ?? undefined })} />
          {/* Emptied → back to today (shown, never a blank box the preview ignores). */}
          <p className={`${NOTE} mt-[5px]`}>Preview only, not saved</p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className={LABEL} htmlFor={`${uid}-kind`}>Settlement</label>
          <select id={`${uid}-kind`} className={INPUT} value={options.kind ?? 'FINAL'} onChange={(e) => setOptions({ ...options, kind: e.target.value as 'FINAL' | 'INTERIM' })}>
            <option value="FINAL">Final</option>
            <option value="INTERIM">Interim</option>
          </select>
        </div>
        </div>
        {options.kind === 'INTERIM'
          ? <div><PenceField label="Retention (held back)" pence={options.retentionPence ?? 0} onPence={(p) => setOptions({ ...options, retentionPence: p ?? 0 })} /></div>
          : (
            <div>
              <label className={LABEL} htmlFor={`${uid}-dest`}>Net damages go to</label>
              <select id={`${uid}-dest`} className={INPUT} value={options.netDestination ?? ''} onChange={(e) => setOptions({ ...options, netDestination: (e.target.value || undefined) as NetDestination | undefined })}>
                <option value="">Automatic</option>
                {(Object.keys(DEST_LABEL) as NetDestination[]).map((d) => <option key={d} value={d}>{DEST_LABEL[d]}</option>)}
              </select>
              {/* The explanation lives under the box, so the box never truncates it. */}
              {!options.netDestination && <p className={`${NOTE} mt-[5px]`}>A child’s damages go to the Court Funds Office; anyone else’s to the client.</p>}
            </div>
          )}
        <label className="flex items-center gap-[8px] text-[13px] text-casevo-forest">
          <input type="checkbox" className="accent-casevo-primary" checked={!!options.shortfallWaived} onChange={(e) => setOptions({ ...options, shortfallWaived: e.target.checked })} />
          Waive the base-costs shortfall for this settlement
        </label>
        {preview && (
          <p className={`${NOTE} m-0`}>
            Success rate used: <b className="font-semibold text-casevo-forest">{ratePct}%</b> · {CFA_STAGE_LABEL[preview.used.cfaStage].toLowerCase()}
            {preview.used.rate.fromStage && preview.used.rate.fromStage !== preview.used.cfaStage ? ` (the ${CFA_STAGE_LABEL[preview.used.rate.fromStage].toLowerCase()} rate)` : ''}
          </p>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <Banner tone="error">
            {error}
            {stale && <span className="block mt-[2px] text-[12px]">The figures below are out of date until this is fixed.</span>}
          </Banner>
        </div>
      )}

      {/* Net to client — large. Same-size placeholder while the first run loads. */}
      <div className="mt-[14px] rounded-[12px] bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] dark:bg-[linear-gradient(135deg,#1b2e24,#223c2e)] text-white px-[18px] py-[14px] min-h-[96px]">
        <div className="flex flex-wrap items-center justify-between gap-[8px]">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] opacity-80" data-testid="net-label">{netLabel}{stale ? ' · out of date' : ''}</div>
          <DraftBadge onDark />
        </div>
        <div className={`text-[34px] leading-[1.15] font-semibold tabular-nums mt-[2px] transition-opacity ${staleCls}`}>
          {net != null ? money(net) : preview && !preview.ok ? '—' : <span className="inline-block h-[30px] w-40 rounded-md bg-white/20 motion-safe:animate-pulse align-middle" />}
        </div>
        {r && (
          <div className={`text-[12.5px] opacity-85 mt-[2px] flex flex-wrap gap-x-[14px] ${staleCls}`}>
            <span>Damages {money(r.figures.totalDamagesPence)}</span>
            <span>Deductions {money(r.figures.totalDeductionsPence)}</span>
          </div>
        )}
        {/* (Batch 2) The CFA check is asked for on the CFA card — one short line here. */}
        {cfaUnreviewed && (
          <p className="m-0 mt-[8px] text-[12px] opacity-90 flex flex-wrap items-center gap-x-[8px]">
            <span>CFA terms not checked yet.</span>
            {onReviewCfa && <button type="button" className="underline underline-offset-2 font-semibold focus:outline-none casevo-focus rounded-[4px]" onClick={onReviewCfa}>Go to the CFA</button>}
          </p>
        )}
      </div>

      {f && <div className={staleCls}><BreakdownBar figures={f} /></div>}

      {/* Blocking errors (red) then warnings (amber). */}
      {preview && (preview.errors.length > 0 || preview.warnings.length > 0) && (
        <div className="mt-3 flex flex-col gap-[8px]">
          {preview.errors.map((i, n) => (i.code === 'NO_CFA' && onCreateCfa
            ? (
              <ActionBox key={`e${n}`} tone="danger" title="Can’t calculate yet — no CFA" ariaLabel="Can’t calculate yet — no CFA"
                message={<>There’s no CFA on this case yet. Create one from the firm’s standard terms, then check it against what the client signed.{createCfaError && <span className="block mt-[4px] text-casevo-danger" role="alert">{createCfaError}</span>}</>}
                actions={[{ label: 'Create from firm defaults', primary: true, onClick: onCreateCfa, busy: creatingCfa, busyLabel: 'Creating…' }]} />
            )
            : <Banner key={`e${n}`} tone="error"><b className="font-semibold">Can’t calculate yet.</b> {i.message}</Banner>))}
          {/* The court-approval note is said once, in the box below (the code stays in the data for round 2b);
              the CFA check once, on the CFA card. */}
          {preview.warnings.filter((i) => !(proposed && i.code === 'COURT_APPROVAL_REQUIRED') && i.code !== 'CFA_NOT_REVIEWED').map((i, n) => <Banner key={`w${n}`} tone="warn"><span style={{ whiteSpace: 'pre-line' }}>{i.message}</span></Banner>)}
        </div>
      )}
      {proposed && (
        <div className="mt-3"><Banner tone="info">This settlement is for a {r!.capacity === 'CHILD' ? 'child' : 'protected party'}, so the court must approve it. Every deduction below is <b className="font-semibold">proposed — subject to court order</b> (CPR 21).</Banner></div>
      )}

      {/* The story. */}
      {!preview && !error ? (
        <div className="flex flex-col mt-[8px]" aria-hidden="true">
          {[1, 2, 4].map((n, g) => (
            <React.Fragment key={g}>
              <Skel className="h-[12px] w-40 mt-[18px] mb-[8px]" />
              {Array.from({ length: n }, (_, i) => <Skel key={i} className="h-[40px] w-full mb-[8px]" />)}
            </React.Fragment>
          ))}
          <Skel className="h-[44px] w-full mt-[10px] rounded-[10px]" />
        </div>
      ) : r && f ? (
        <div className={`${stale ? staleCls : loading ? 'opacity-60' : ''} transition-opacity`}>
          <StoryHead n={1}>Damages</StoryHead>
          <StoryRow minus={false} title="Total damages" amount={money(f.totalDamagesPence)}
            why={heads ? [
              heads.pslaPence > 0 && `PSLA ${pounds0(heads.pslaPence)}`, heads.pastLossPence > 0 && `past loss ${pounds0(heads.pastLossPence)}`,
              heads.futureLossPence > 0 && `future loss ${pounds0(heads.futureLossPence)}`, heads.interestPence > 0 && `interest ${pounds0(heads.interestPence)}`,
            ].filter(Boolean).join(' · ') : undefined} />

          {((cruRow?.finalPence ?? 0) > 0 || f.interimPaymentsPence > 0 || f.retentionPence > 0) && (
            <>
              <StoryHead n={2}>Less, paid out already</StoryHead>
              {(cruRow?.finalPence ?? 0) > 0 && (
                <StoryRow title="CRU repaid" amount={`−${money(cruRow!.finalPence)}`} was={cruRow!.reductions.length ? `−${money(cruRow!.amountPence)}` : null}
                  why={cruRow!.reductions.length ? 'Benefits repaid to the DWP — only up to the matching past loss' : 'Benefits repaid to the DWP from the matching past loss'} />
              )}
              {f.interimPaymentsPence > 0 && (
                <StoryRow title={interimRow && /^(\d+) payment/.test(interimRow.rule) && Number(interimRow.rule.match(/^(\d+)/)![1]) > 1 ? 'Interim payments' : 'Interim payment'}
                  amount={`−${money(f.interimPaymentsPence)}`} why="Already paid to the client" />
              )}
              {f.retentionPence > 0 && <StoryRow title="Retention" amount={`−${money(f.retentionPence)}`} why="Held back from this interim settlement" />}
            </>
          )}

          <StoryHead n={(cruRow?.finalPence ?? 0) > 0 || f.interimPaymentsPence > 0 || f.retentionPence > 0 ? 3 : 2}>
            Deductions{firmCapLabel && f.firmCapPence != null ? ` (firm cap ${firmCapLabel} = ${pounds0(f.firmCapPence)})` : ''}
          </StoryHead>
          {deductionRows.length === 0
            ? <p className={`${NOTE} py-[8px] m-0`}>Nothing is deducted.</p>
            : deductionRows.map((row) => (
              <StoryRow key={row.key}
                title={<span className="inline-flex flex-wrap items-center gap-[6px]">{STORY_LABEL[row.key] ?? row.label}{proposed && <ProposedTag />}</span>}
                why={whyOf(row, { ratePct, capBase: f.capBasePence, capPct })}
                amount={`−${money(row.finalPence)}`}
                was={row.reductions.length ? `−${money(row.amountPence)}` : null} />
            ))}
          {creditRow && (
            <StoryRow minus={false} title={creditRow.label} amount={`+${money(creditRow.finalPence)}`}
              why={f.costsCreditToNetPence ? `Clears the deductions; ${money(f.costsCreditToNetPence)} is added to the net sum` : 'Paid on interim bills and covered by the other side too'} />
          )}
          {disbursementCreditRow && (
            <StoryRow minus={false} title={disbursementCreditRow.label} amount={`+${money(disbursementCreditRow.finalPence)}`}
              why={`Paid on interim bills and paid by the other side too. ${CONFIRM_WITH_SOLICITOR}`} />
          )}
          <StoryRow minus={false} mark={<SIcon name="check" size={11} />} title="Total deductions" amount={`−${money(f.totalDeductionsPence)}`}
            why={f.firmCapPence != null ? `Within the firm’s cap of ${money(f.firmCapPence)}` : proposed ? 'Proposed — the court decides' : undefined} />
          <div className="mt-[10px] flex items-center justify-between rounded-[10px] bg-casevo-mint px-[16px] py-[11px]">
            <span className="text-[14px] font-semibold text-casevo-forest">{netLabel}</span>
            <span className="text-[15px] font-semibold tabular-nums text-casevo-forest">{money(f.netToClientPence)}</span>
          </div>

          {/* The engine's own rows + rules, for anyone checking the maths. */}
          <details className="mt-[12px] group">
            <summary className="cursor-pointer text-[12.5px] font-semibold text-casevo-brand dark:text-casevo-link focus:outline-none casevo-focus rounded-[4px] list-none">
              <span className="inline-flex items-center gap-[4px]"><BIcon name="chevr" size={12} className="transition-transform group-open:rotate-90" />Full calculation</span>
            </summary>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[12.5px] mt-[6px]">
                <tbody>
                  {r.rows.map((row, i) => {
                    const head = i === 0 || r.rows[i - 1].section !== row.section
                    return (
                      <React.Fragment key={row.key}>
                        {head && <tr><td colSpan={2} className="pt-[10px] pb-[3px] text-[10.5px] font-bold uppercase tracking-[0.12em] text-casevo-text-soft">{SECTION_LABEL[row.section]}</td></tr>}
                        <tr className="align-top">
                          <td className="py-[5px] pr-[10px] border-b border-casevo-border-soft">
                            <div className="text-casevo-forest">{row.label}</div>
                            <div className="text-[11.5px] text-casevo-text-soft">{row.rule}</div>
                            {row.reductions.map((red, n) => <div key={n} className="text-[11.5px] text-casevo-warn">− {money(red.pence)} · {red.rule}</div>)}
                          </td>
                          <td className="py-[5px] border-b border-casevo-border-soft text-right tabular-nums whitespace-nowrap text-casevo-forest">
                            {row.key === 'COSTS_POSITION' && row.amountPence < 0 ? `−${money(-row.amountPence)}` : row.reductions.length || row.section === 'TOTAL' ? money(row.finalPence) : money(row.amountPence)}
                          </td>
                        </tr>
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
              <p className={`${NOTE} mt-[8px]`}>Engine {r.engineVersion}. Figures are rounded down in the client’s favour. Draft only — not for client use; saving and issuing a settlement comes next.</p>
            </div>
          </details>
        </div>
      ) : (
        <p className={`${NOTE} mt-[12px]`}>Fix the problem above to see the deductions.</p>
      )}
    </>
  )
}

// The preview CARD (the side column, when the section has room).
const SettlementPreview: React.FC<Props> = (props) => (
  <section className={CARD} aria-label="Settlement preview" aria-busy={props.loading || undefined}>
    <div className="flex items-start gap-3 px-5 pt-4 pb-3">
      <div className="w-[34px] h-[34px] rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-casevo-link grid place-items-center shrink-0"><BIcon name="zap" /></div>
      <div className="flex-1 min-w-0">
        <h3 className="m-0 text-[15.5px] font-semibold text-casevo-forest">Deductions preview</h3>
        <p className="m-0 mt-[2px] text-[12.5px] text-casevo-text-soft">Live, from the saved figures{props.unsaved ? ' and the card you’re editing' : ''}</p>
        <div className="mt-[6px] flex flex-wrap gap-[6px]"><DraftBadge />{props.unsaved && <UnsavedBadge />}</div>
      </div>
      {props.loading && <Spinner />}
    </div>
    <div className="px-5 pb-5"><PreviewBody {...props} /></div>
  </section>
)

// A narrow section: a sticky bar at the bottom of the section — Net to client +
// "Show breakdown" (the full preview in a modal). It sits AFTER the last card
// (sticky bottom), so it never covers a card's content.
export const SettlementBottomBar: React.FC<Props & { open: boolean; setOpen: (o: boolean) => void }> = ({ open, setOpen, ...props }) => {
  const r = props.preview?.result ?? null
  const stale = !!props.error && !!r
  const netLabel = netLabelOf(r?.capacity, props.options.kind === 'INTERIM' ? undefined : props.options.netDestination, props.payee)
  return (
    <>
      {/* Sticky is applied by the parent wrapper (it must span the column for sticky to engage); the modal is portalled out of that stacking context. */}
      <div className="mt-4 -mx-4 sm:mx-0 border-t sm:border border-casevo-border bg-casevo-surface sm:rounded-[14px] shadow-[0_-6px_18px_rgba(16,42,29,0.08)] dark:shadow-[0_-6px_18px_rgba(0,0,0,0.3)] px-5 py-[10px] flex items-center gap-[16px] flex-wrap" role="region" aria-label="Settlement summary">
        <div className={stale ? 'opacity-40 grayscale' : ''}>
          <div className="flex items-center flex-wrap gap-x-[10px] gap-y-[4px]"><span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-casevo-text-soft" data-testid="bar-net-label">{netLabel}{stale ? ' · out of date' : ''}</span><DraftBadge />{props.unsaved && <UnsavedBadge />}</div>
          <div className="flex items-baseline flex-wrap gap-x-[12px]">
            <span className="text-[24px] leading-tight font-semibold tabular-nums text-casevo-forest">{r ? money(r.figures.netToClientPence) : '—'}</span>
            {r && <span className="text-[13px] text-casevo-text-soft tabular-nums">Deductions {money(r.figures.totalDeductionsPence)}</span>}
          </div>
        </div>
        <div className="ml-auto">
          <button type="button" className={B_PRIMARY} onClick={() => setOpen(true)}><BIcon name="chev" size={14} className="rotate-180" />Show breakdown</button>
        </div>
      </div>
      <ModalFrame isOpen={open} onClose={() => setOpen(false)} size="lg" portal eyebrow="Settlement" title="Deductions preview" subtitle={`Live, from the saved figures${props.unsaved ? ' and the card you’re editing' : ''} — draft, not for client use.`}>
        <PreviewBody {...props} />
      </ModalFrame>
    </>
  )
}

export default SettlementPreview
